import React, { useState, useEffect, useRef } from 'react';
import { EventEmitter } from 'events';
import BackroomHLSPlayer, { getUrl } from './BackroomHLSPlayer';
import { VideoStats } from '../cockpit/Cockpit';
import ReactPlayer from 'react-player';
import VolumeControls from '../party/VolumeControls';

const DEFAULT_TRANSITION_LENGTH = 10000;

enum TransitionStates {
  NEW = 'NEW',
  SHOW_STREAM_A = 'SHOW_STREAM_A',
  SHOW_STREAM_B = 'SHOW_STREAM_B',
  PREPARING_TRANSITION = 'PREPARING_TRANSITION',
  TRANSITION_READY = 'TRANSITION_READY',
  TRANSITION_IN_PROGRESS = 'TRANSITION_IN_PROGRESS',
}

enum TransitionEvents {
  INIT_MAIN = 'INIT_MAIN',
  INIT_INCOMING = 'INIT_INCOMING',
  RECEIVE_ID = 'RECEIVE_ID',
  INCOMING_STREAM_MOUNTING = 'INCOMING_STREAM_MOUNTING',
  STREAM_PLAYING = 'STREAM_PLAYING',
  INCOMING_PLAYING = 'INCOMING_PLAYING',
  TRANSITION_TICK = 'TRANSITION_TICK',
  TRANSITION_COMPLETE = 'TRANSITION_COMPLETE',
}

type Streams = { [key in StreamName]: Stream };

function useTransition(
  id: string,
  transitionLength?: number,
  onLoadVideoElement?: (videoEl: HTMLVideoElement) => void
) {
  const [values, onUpdate] = useState<Streams>({
    A: { id: null, opacity: 1, primary: true, onLoad: () => {} },
    B: { id: null, opacity: 0, primary: false, onLoad: () => {} },
  });
  const transitionController = useRef<TransitionController>(
    new TransitionController({
      onUpdate,
      transitionTimeInMs: transitionLength || DEFAULT_TRANSITION_LENGTH,
      onLoadVideoElement,
    })
  );

  useEffect(() => {
    if (!transitionController.current) {
      transitionController.current = new TransitionController({
        onUpdate,
        transitionTimeInMs: 10000,
      });
    }
    return () => transitionController.current?.destroy();
  }, [onUpdate]);

  transitionController?.current?.onRender(id, transitionLength);

  return values;
}

type Stream = {
  id: string | null;
  opacity: number;
  primary: boolean;
  onLoad: (videoEl: HTMLVideoElement) => void;
};

type StreamName = 'A' | 'B';

class TransitionController extends EventEmitter {
  private state: TransitionStates = TransitionStates.NEW;
  private streams: { [key in 'A' | 'B']: Stream };
  private onUpdate: (values: { [key in StreamName]: Stream }) => void;
  private transitionTimeInMs: number;
  private timeoutHandle: NodeJS.Timeout | null = null;
  private onLoadVideoElement: ((videoEl: HTMLVideoElement) => void) | undefined;

  constructor({
    onUpdate,
    transitionTimeInMs,
    onLoadVideoElement,
  }: {
    onUpdate: (values: { [key in StreamName]: Stream }) => void;
    transitionTimeInMs: number;
    onLoadVideoElement?: (videoEl: HTMLVideoElement) => void;
  }) {
    super();
    this.onUpdate = onUpdate;
    this.transitionTimeInMs = transitionTimeInMs;
    this.onLoadVideoElement = onLoadVideoElement;

    this.streams = {
      A: { id: null, opacity: 1, primary: true, onLoad: this.onLoadStreamA },
      B: { id: null, opacity: 0, primary: false, onLoad: this.onLoadStreamB },
    };

    this.on(TransitionEvents.INIT_MAIN, (id: string, streamName: StreamName) => {
      this.streams[this.getPimaryStreamName()].id = id;
      this.state = TransitionStates.SHOW_STREAM_A;
      this.timeToUpdate();
    });

    this.on(TransitionEvents.INIT_INCOMING, (id: string) => {
      this.cancelTransition();
      this.streams[this.getSecondaryStreamName()].id = id;
      this.state = TransitionStates.PREPARING_TRANSITION;
      this.timeToUpdate();
    });

    this.on(TransitionEvents.STREAM_PLAYING, (streamName: StreamName) => {
      if (this.getSecondaryStreamName() === streamName) {
        this.state = TransitionStates.TRANSITION_IN_PROGRESS;
        this.transitionTick(0);
      }
    });

    this.on(TransitionEvents.TRANSITION_COMPLETE, () => {
      if (this.streams.A.primary) {
        this.state = TransitionStates.SHOW_STREAM_B;
        this.streams.A.primary = false;
        this.streams.A.opacity = 0;
        this.streams.A.id = null;
        this.streams.B.primary = true;
        this.streams.B.opacity = 1;
      } else {
        this.state = TransitionStates.SHOW_STREAM_A;
        this.streams.A.primary = true;
        this.streams.A.opacity = 1;
        this.streams.B.primary = false;
        this.streams.B.opacity = 0;
        this.streams.B.id = null;
      }
      this.timeToUpdate();
    });
  }

  private cancelTransition() {
    clearTimeout(this.timeoutHandle as NodeJS.Timeout);
  }

  private transitionTick(completion: number) {
    const STEP_SIZE_MS = 20;
    const incrementAmount = STEP_SIZE_MS / this.transitionTimeInMs;
    if (completion >= 1) {
      clearTimeout(this.timeoutHandle as NodeJS.Timeout);
      this.emit(TransitionEvents.TRANSITION_COMPLETE);
    } else {
      this.streams[this.getPimaryStreamName()].opacity = 1 - completion;
      this.streams[this.getSecondaryStreamName()].opacity = completion;
      this.timeToUpdate();
      this.timeoutHandle = setTimeout(
        () => this.transitionTick(completion + incrementAmount),
        STEP_SIZE_MS
      );
    }
  }

  private getPimaryStreamName(): StreamName {
    if (this.streams.A.primary) {
      return 'A';
    }
    return 'B';
  }

  private getSecondaryStreamName(): StreamName {
    if (this.streams.A.primary) {
      return 'B';
    }
    return 'A';
  }

  private timeToUpdate() {
    this.onUpdate({ ...this.streams });
  }

  onLoadStreamA = (videoEl: HTMLVideoElement) => {
    this.emit(TransitionEvents.STREAM_PLAYING, 'A');
    if (this.onLoadVideoElement) {
      this.onLoadVideoElement(videoEl);
    }
  };

  onLoadStreamB = (videoEl: HTMLVideoElement) => {
    this.emit(TransitionEvents.STREAM_PLAYING, 'B');
    if (this.onLoadVideoElement) {
      this.onLoadVideoElement(videoEl);
    }
  };

  onRender(id: string | null, transitionLength?: number) {
    this.transitionTimeInMs = transitionLength || DEFAULT_TRANSITION_LENGTH;
    let primaryStreamId;
    let incomingStreamId;
    const primaryName: StreamName = this.getPimaryStreamName();
    if (primaryName === 'A') {
      primaryStreamId = this.streams.A.id;
      incomingStreamId = this.streams.B.id;
    } else {
      primaryStreamId = this.streams.B.id;
      incomingStreamId = this.streams.A.id;
    }

    // don't interrupt a transition in progress
    if (this.state === TransitionStates.TRANSITION_IN_PROGRESS) {
      return;
    }

    // we already know about it, ignore
    if (id === primaryStreamId || id === incomingStreamId) {
      return;
    }

    // haven't set the id yet, state should be NEW
    if (!primaryStreamId) {
      this.emit(TransitionEvents.INIT_MAIN, id);
      return;
    }

    // if we get here, we have a new id, begin the process
    this.emit(TransitionEvents.INIT_INCOMING, id);
  }

  destroy() {
    clearTimeout(this.timeoutHandle as NodeJS.Timeout);
  }
}

function BackroomVideo({
  volume = 1,
  id,
  transitionLength,
  height,
  width,
  muted = false,
  controls = true,
  onLoadVideoElement,
  onStatsUpdated,
  audioSource,
  fullscreen,
}: {
  id: string;
  controls?: boolean;
  volume?: number;
  test?: boolean;
  transitionLength?: number;
  height: number | string;
  width: number | string;
  muted?: boolean;
  onLoadVideoElement?: (videoEl: HTMLVideoElement) => void;
  onStatsUpdated?: (obj: VideoStats) => void;
  audioSource?: string;
  fullscreen?: boolean;
}) {
  const { A, B } = useTransition(id, transitionLength, onLoadVideoElement);
  const isStaticAudioSource = audioSource && audioSource !== 'audio-follows-video';

  const style: React.CSSProperties = {
    height,
    width,
    position: 'relative',
    display: 'flex',
  };
  if (fullscreen) {
    style.position = 'fixed';
  }

  return (
    <div style={style}>
      {isStaticAudioSource && (
        <ReactPlayer
          className='react-player'
          playing
          controls={true}
          config={{
            file: {
              forceHLS: true,
            },
          }}
          url={getUrl(audioSource!)}
          parent={[
            'dancefloor-app.web.com',
            'parent=ingest.backroom.social',
            'fb-towers.firebaseapp.com',
            'localhost:3000',
            'localhost',
          ]}
          style={{
            display: 'none',
          }}
          height={height}
          // width={width}
          volume={volume}
        />
      )}
      <BackroomHLSPlayer
        muted={muted}
        opacity={A.opacity}
        onLoadComplete={A.onLoad}
        key={'A'}
        videoId={A.id}
        controls={controls}
        volume={isStaticAudioSource ? 0 : volume * A.opacity}
        style={{ opacity: A.opacity }}
        videoProps={{ muted: true }}
        height={height}
        onStatsUpdated={onStatsUpdated}
        fullscreen={fullscreen}
      />
      <BackroomHLSPlayer
        muted={muted}
        onLoadComplete={B.onLoad}
        opacity={B.opacity}
        secondary
        key={'B'}
        videoId={B.id}
        controls={controls}
        volume={isStaticAudioSource ? 0 : volume * B.opacity}
        style={{ opacity: B.opacity }}
        videoProps={{ muted: true }}
        height={height}
        onStatsUpdated={onStatsUpdated}
        fullscreen={fullscreen}
      />
    </div>
  );
}

export default BackroomVideo;
